/**
 * @fileoverview Componente responsável por exibir novidades em um painel lateral.
 */

import React, { useState, useEffect, useContext, useCallback, useRef } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Drawer from "@material-ui/core/Drawer";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import CircularProgress from "@material-ui/core/CircularProgress";
import { X, Bell, Megaphone } from "lucide-react";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({
  drawer: {
    width: 500,
    flexShrink: 0,
    [theme.breakpoints.down('sm')]: {
      width: '100vw',
    },
  },
  drawerPaper: {
    width: 500,
    fontFamily: "'Montserrat', sans-serif",
    border: 'none',
    borderLeft: '1px solid #3d4047',
    background: 'linear-gradient(180deg, #121213 0%, #16161a 100%)',
    boxShadow: '-8px 0 40px rgba(0, 0, 0, 0.5)',
    [theme.breakpoints.down('sm')]: {
      width: '100vw',
    },
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(2.5, 3),
    borderBottom: '1px solid #3d4047',
    background: 'rgba(91, 124, 255, 0.04)',
  },
  headerTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
  },
  headerIcon: {
    color: '#5B7CFF',
  },
  title: {
    fontSize: '1.25rem',
    fontWeight: 700,
    color: '#FFFFFF',
  },
  subtitle: {
    fontSize: '0.78rem',
    color: 'rgba(255, 255, 255, 0.4)',
    marginTop: 2,
    fontWeight: 400,
  },
  closeButton: {
    color: 'rgba(255, 255, 255, 0.5)',
    '&:hover': {
      background: 'rgba(255, 255, 255, 0.08)',
    },
  },
  content: {
    flex: 1,
    overflowY: 'auto',
    padding: theme.spacing(2),
    ...theme.scrollbarStyles,
  },
  postCard: {
    background: 'rgba(255, 255, 255, 0.03)',
    border: '1px solid #3d4047',
    borderRadius: 16,
    padding: theme.spacing(2.5),
    marginBottom: theme.spacing(2),
    transition: 'all 0.2s ease',
    '&:hover': {
      border: '1px solid #3d4047',
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
    },
  },
  postCardInner: {
    display: 'flex',
    gap: theme.spacing(1.5),
    alignItems: 'flex-start',
  },
  postIconWrapper: {
    flexShrink: 0,
    width: 38,
    height: 38,
    borderRadius: '50%',
    backgroundColor: '#1b253f',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 2,
  },
  postBody: {
    flex: 1,
    minWidth: 0,
  },
  postTitle: {
    fontSize: '1.05rem',
    fontWeight: 500,
    color: '#e6e6e6',
    marginBottom: theme.spacing(0.5),
    lineHeight: 1.3,
  },
  postMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    marginBottom: theme.spacing(1.5),
  },
  postAuthor: {
    fontSize: '0.8rem',
    fontWeight: 500,
    color: 'rgba(255, 255, 255, 0.5)',
  },
  postDate: {
    fontSize: '0.8rem',
    color: 'rgba(255, 255, 255, 0.35)',
  },
  postImage: {
    width: '100%',
    borderRadius: 12,
    marginBottom: theme.spacing(1.5),
    objectFit: 'cover',
    maxHeight: 200,
    background: 'rgba(255, 255, 255, 0.05)',
  },
  postText: {
    fontSize: '0.9rem',
    color: '#c9c9c9b3',
    lineHeight: 1.6,
    '& p': {
      margin: '0 0 8px 0',
    },
    '& ul, & ol': {
      margin: '0 0 8px 0',
      paddingLeft: 20,
    },
    '& a': {
      color: '#5B7CFF',
    },
    '& img': {
      maxWidth: '100%',
      borderRadius: 8,
    },
  },
  postTextExpanded: {
    maxHeight: 2000,
    overflow: 'hidden',
    transition: 'max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  },
  postTextCollapsed: {
    maxHeight: 140,
    overflow: 'hidden',
    position: 'relative',
    borderRadius: 14,
    transition: 'max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
    '&::after': {
      content: '""',
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: 0,
      height: 40,
      background: 'linear-gradient(transparent, #151516)',
    },
  },
  readMoreBtn: {
    fontFamily: "'Montserrat', sans-serif",
    color: '#1576db',
    fontSize: '0.85rem',
    fontWeight: 600,
    cursor: 'pointer',
    border: 'none',
    background: 'none',
    padding: 0,
    marginTop: theme.spacing(0.5),
    transition: 'opacity 0.2s ease',
    '&:hover': {
      opacity: 0.75,
      textDecoration: 'underline',
    },
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(6),
    textAlign: 'center',
  },
  emptyIcon: {
    color: 'rgba(255, 255, 255, 0.15)',
    marginBottom: theme.spacing(2),
  },
  emptyText: {
    color: 'rgba(255, 255, 255, 0.4)',
    fontSize: '0.95rem',
  },
  loader: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(3),
  },
}));

const formatRelativeDate = (dateStr) => {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMin = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMin < 1) return "agora";
  if (diffMin < 60) return `há ${diffMin} min`;
  if (diffHours < 24) return `há ${diffHours}h`;
  if (diffDays === 1) return "há 1 dia";
  if (diffDays < 30) return `há ${diffDays} dias`;
  const diffMonths = Math.floor(diffDays / 30);
  if (diffMonths === 1) return "há 1 mês";
  if (diffMonths < 12) return `há ${diffMonths} meses`;
  const diffYears = Math.floor(diffDays / 365);
  if (diffYears === 1) return "há 1 ano";
  return `há ${diffYears} anos`;
};

const backendUrl = process.env.REACT_APP_BACKEND_URL;

const NewsDrawer = ({ open, onClose }) => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);

  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedPosts, setExpandedPosts] = useState({});
  const cardRefs = useRef({});

  const fetchPosts = useCallback(async () => {
    if (!open) return;
    setLoading(true);
    try {
      const { data } = await api.get("/news-posts/list", {
        params: { companyId: user.companyId },
      });
      setPosts(data || []);
    } catch (err) {
      toastError(err);
    }
    setLoading(false);
  }, [open, user.companyId]);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  useEffect(() => {
    if (open) {
      localStorage.setItem("newsPostsLastSeen", new Date().toISOString());
    }
  }, [open]);

  const toggleExpanded = (id) => {
    const isExpanding = !expandedPosts[id];
    setExpandedPosts((prev) => ({ ...prev, [id]: !prev[id] }));
    if (isExpanding && cardRefs.current[id]) {
      setTimeout(() => {
        const card = cardRefs.current[id];
        const container = card.closest('[class*="content"]');
        if (container) {
          const cardTop = card.offsetTop - container.offsetTop;
          container.scrollTo({ top: cardTop - 5, behavior: 'smooth' });
        } else {
          card.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 50);
    }
  };

  const isLongText = (html) => {
    if (!html) return false;
    const tmp = document.createElement("div");
    tmp.innerHTML = html;
    const text = tmp.textContent || "";
    return text.length > 150;
  };

  return (
    <Drawer
      className={classes.drawer}
      anchor="right"
      open={open}
      onClose={onClose}
      classes={{ paper: classes.drawerPaper }}
    >
      <div className={classes.header}>
        <div className={classes.headerTitle}>
          <div>
            <Typography className={classes.title}>Novidades</Typography>
            <Typography className={classes.subtitle}>Fique por dentro das últimas atualizações</Typography>
          </div>
        </div>
        <IconButton size="small" onClick={onClose} className={classes.closeButton}>
          <X size={20} />
        </IconButton>
      </div>

      <div className={classes.content}>
        {loading ? (
          <div className={classes.loader}>
            <CircularProgress size={32} />
          </div>
        ) : posts.length === 0 ? (
          <div className={classes.emptyState}>
            <Bell size={48} className={classes.emptyIcon} />
            <Typography className={classes.emptyText}>
              Nenhuma novidade por aqui ainda.
            </Typography>
          </div>
        ) : (
          posts.map((post) => (
            <div key={post.id} ref={(el) => (cardRefs.current[post.id] = el)} className={classes.postCard}>
              <div className={classes.postCardInner}>
                <div className={classes.postIconWrapper}>
                  <Megaphone size={22} strokeWidth={1.25} color="#1f88f6" />
                </div>
                <div className={classes.postBody}>
                  <Typography className={classes.postTitle}>
                    {post.title}
                  </Typography>
                  <div className={classes.postMeta}>
                    <span className={classes.postAuthor}>
                      {post.author || post.user?.name || "Equipe"}
                    </span>
                    <span className={classes.postDate}>
                      - {formatRelativeDate(post.createdAt)}
                    </span>
                  </div>
                </div>
              </div>

              {post.mediaPath && (
                <img
                  src={`${backendUrl}/public/${post.mediaPath}`}
                  alt={post.title}
                  className={classes.postImage}
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              )}

              {post.text && (
                <>
                  <div
                    className={`${classes.postText} ${
                      isLongText(post.text)
                        ? expandedPosts[post.id]
                          ? classes.postTextExpanded
                          : classes.postTextCollapsed
                        : ''
                    }`}
                    dangerouslySetInnerHTML={{ __html: post.text }}
                  />
                  {isLongText(post.text) && (
                    <button
                      className={classes.readMoreBtn}
                      onClick={() => toggleExpanded(post.id)}
                    >
                      {expandedPosts[post.id] ? "Ver menos" : "Ver mais"}
                    </button>
                  )}
                </>
              )}
            </div>
          ))
        )}
      </div>
    </Drawer>
  );
};

export default NewsDrawer;
